/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { type TCreateSessionParams } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../device";
import { SessionInteractor } from "../../interactor";
import { Log, Diagnostics } from "../../logging";
import { PlaybackContentType, SessionState } from "../enums";
import { PlaybackSetupErrorCode, SessionFailureClassifier } from "../error";
import type { ISessionCreateModel } from "../interfaces";
import { VideoProviderConfigModel } from "../../videoProvider";
import { StreamingSessionImpl } from "./StreamingSessionImpl";
import { Locale, LocaleKeys } from "../../locale";

/**
 * DRM type for an unencrypted stream.
 *
 * The session request requires a DRM type, so a provider that delivers in the
 * clear still has to name one. Saying so explicitly is the point: the previous
 * default claimed Widevine for streams that carry no protection at all.
 */
const CLEAR_DRM_TYPE: string = "clear";

/**
 * Streaming session implementation for linear content types:
 * LIVE, STARTOVER_BEGINNING, STARTOVER_RESUME, CATCHUP_BEGINNING, CATCHUP_RESUME.
 */
export class LinearStreamingSessionImpl extends StreamingSessionImpl {
  protected readonly TAG: string = "LinearStreamingSessionImpl";

  public async createStreamingSession(
    sessionCreateModel: ISessionCreateModel
  ): Promise<void> {
    const { channel, drmType, transportEncodingType } = sessionCreateModel;

    if (!channel) return;

    const { partnerStationId, linearProviderPartnerId } = channel;

    if (!linearProviderPartnerId) {
      this.log(
        "error",
        "createStreamingSession(): missing linearProviderPartnerId"
      );
      this.mError = this.getPlaybackError(
        PlaybackSetupErrorCode.DEVICE_MISSING_CONFIG,
        Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_MESSAGE),
        Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_TITLE)
      );
      this.mSessionState = SessionState.ERROR;
      return;
    }

    try {
      const tsn = await DeviceModel.getTiVoSettings().getBodyId();
      const sessionParams: TCreateSessionParams = {
        deviceType:
          DeviceModel.getTiVoSettings().getDeviceType() as TCreateSessionParams["deviceType"],
        drmType: LinearStreamingSessionImpl.resolveDrmType(
          drmType,
          linearProviderPartnerId
        ) as TCreateSessionParams["drmType"],
        partnerId: linearProviderPartnerId,
        partnerServicesCustomerId: await DeviceModel.getPartnerCustomerId(),
        partnerServicesBodyId: await DeviceModel.getCaDeviceId(),
        partnerStationId: partnerStationId || "",
        transportEncodingType:
          LinearStreamingSessionImpl.resolveTransportEncodingType(
            transportEncodingType,
            linearProviderPartnerId
          ) as TCreateSessionParams["transportEncodingType"],
        tsn
      };

      this.log(
        "info",
        `createStreamingSession(): contentType=${sessionCreateModel.playbackContentType}`
      );

      const session = await new SessionInteractor().createSession(
        sessionParams
      );
      this.mDomainSession = session;
      const playlistUrl = LinearStreamingSessionImpl.resolvePlaylistUrl(
        session.playlistUrl,
        sessionCreateModel.playUri
      );

      if (playlistUrl === "") {
        // Recorded but not fatal. The pre-session URL resolved from the tuning
        // URI is already playing, and tearing that down over a field the
        // service should have sent would punish the viewer for a server-side
        // contract violation they cannot do anything about.
        this.log(
          "warn",
          "createStreamingSession(): session carried no playlist URL (" +
            PlaybackSetupErrorCode.PLAYLIST_URI_MISSING +
            "); continuing on the pre-session URL"
        );
      }

      this.mSession = {
        playlistUrl: playlistUrl,
        trickModeRestrictions:
          (session.trickModeRestrictions as Record<string, unknown>) || {},
        firstKeepAliveSeconds: session.firstKeepAliveSeconds,
        keepAliveIntervalSeconds: session.keepAliveIntervalSeconds || 0,
        localityId: LinearStreamingSessionImpl.resolveLocalityId(session)
      };
      this.mSessionState = SessionState.SUCCESS;
    } catch (error: unknown) {
      this.handleCreateFailure(error, linearProviderPartnerId);
    }
  }

  /**
   * Records a session-create failure and reports whether it is survivable.
   *
   * Network loss keeps its existing meaning — the grace period owns that case
   * and is unchanged. Beyond it, only providers whose streams carry no
   * entitlement of their own are allowed to fail open; for every other
   * provider a failed session still stops playback, exactly as before.
   *
   * This is the only place that decision is made. `FAILED_OPEN` is acted on
   * as issued — the controller does not second-guess it — so a state set here
   * is a commitment that playback continues, not a suggestion.
   */
  private handleCreateFailure(error: unknown, providerPartnerId: string): void {
    if (LinearStreamingSessionImpl.isNetworkUnavailable(error)) {
      this.mSessionState = SessionState.UNREACHABLE;
      return;
    }

    const mayFailOpen =
      VideoProviderConfigModel.isTivoPlusProviderPartnerId(providerPartnerId);

    if (!mayFailOpen) {
      this.log("error", "createStreamingSession()", error);
      this.mError = this.getSessionError(error, PlaybackContentType.LIVE);
      this.mSessionState = SessionState.ERROR;
      return;
    }

    const failure = SessionFailureClassifier.classify(error);

    this.log(
      "error",
      "createStreamingSession(): " +
        failure.reason +
        " for provider " +
        providerPartnerId +
        " — " +
        (failure.failOpen ? "continuing playback" : "blocking playback"),
      error
    );

    this.mError = this.getSessionError(error, PlaybackContentType.LIVE);
    this.mSessionState = failure.failOpen
      ? SessionState.FAILED_OPEN
      : SessionState.ERROR;
  }

  /** Whether session creation failed because the device is offline. */
  private static isNetworkUnavailable(error: unknown): boolean {
    if (typeof error !== "object" || error === null || !("message" in error)) {
      return false;
    }

    const { message } = error as { message: unknown };

    return message === "Network unavailable";
  }

  /**
   * The DRM type to request a session for.
   *
   * Prefers what the player resolved, then what the provider declares, and
   * names the stream clear only when neither offers a DRM type. The previous
   * fallback claimed Widevine unconditionally, so a provider delivering
   * unencrypted content silently asked for a Widevine session.
   */
  private static resolveDrmType(
    drmType: string | undefined,
    providerPartnerId: string
  ): string {
    if (drmType !== undefined && drmType !== "") {
      return drmType;
    }

    const configured =
      VideoProviderConfigModel.getDrmTypeById(providerPartnerId);

    if (configured !== undefined) {
      return configured;
    }

    Diagnostics.warn(
      "LinearStreamingSessionImpl",
      "resolveDrmType(): provider " +
        providerPartnerId +
        " declares no DRM; requesting a clear session"
    );
    Log.w(
      "LinearStreamingSessionImpl",
      "resolveDrmType(): provider " +
        providerPartnerId +
        " declares no DRM; requesting a clear session"
    );

    return CLEAR_DRM_TYPE;
  }

  /**
   * The transport encoding to request a session for, falling back to the
   * provider's own configuration when the player resolved none.
   */
  private static resolveTransportEncodingType(
    transportEncodingType: string | undefined,
    providerPartnerId: string
  ): string | undefined {
    if (transportEncodingType !== undefined && transportEncodingType !== "") {
      return transportEncodingType;
    }

    const configured =
      VideoProviderConfigModel.getTransportEncodingTypesById(providerPartnerId);

    return configured.length > 0 ? configured[0] : transportEncodingType;
  }
}
